import AlertCircle from "@untitled-ui/icons-react/build/esm/AlertCircle";
import Palette from "@untitled-ui/icons-react/build/esm/Palette";
import Plus from "@untitled-ui/icons-react/build/esm/Plus";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import BtnLargeSolid from "../../../../common/buttons/BtnLargeSolid";
import CheckboxVector from "../../../../common/checkbox/CheckboxVector";
import FormField from "../../../../common/forms/FormField";
import SimpleLoadingSpinner from "../../../../common/loaders/SimpleLoadingSpinner";
import FullScreenOverlay from "../../../../common/overlay/FullScreenOverlay";
import TooltipWithInfoCircle from "../../../../common/tooltip/TooltipWithInfoCircle";
import { areColorsMapped, getColorMappingStatus, getPOSColorNames, isPOSColorCatalogueAvailable } from "../colorMappingState";

// #region CONSTANTS
const EMPTY_ARRAY = [];
// #endregion

// #region GLOBAL METHODS
const getConfidenceStatus = colorMapping => {
    if (typeof colorMapping?.confidence === "number") return "ai";
    if (colorMapping?.exportName) return "saved";
    return "none";
};

const getConfidenceDisplay = color => {
    if (color?.confidenceStatus === "review") return { value: "Review", color: "var(--text-and-icons-orange-500, #ff9900)" };
    if (color?.confidenceStatus === "ai" && typeof color?.confidence === "number") {
        return {
            value: `${color.confidence}%`,
            color:
                color.confidence <= 60
                    ? "var(--text-and-icons-orange-500, #ff9900)"
                    : "var(--text-and-icons-green-500, #00bb00)"
        };
    }

    if (color?.confidenceStatus === "saved") {
        return {
            value: "Saved",
            color: "var(--text-and-icons-green-500, #00bb00)"
        };
    }

    return {
        value: "--",
        color: "var(--text-and-icons-gray-300, #4f5b6d)"
    };
};

const buildColorRows = ({ labelBrandColors, colorMappings, includeCheckboxState }) =>
    labelBrandColors.map(colorName => {
        const labelBrandName = colorName ?? "";
        const savedColorMapping = colorMappings.find(mapping => mapping?.labelBrandName === labelBrandName);

        const row = {
            labelBrandName,
            exportName: savedColorMapping?.exportName ?? "",
            confidence: savedColorMapping?.confidence,
            confidenceStatus: getConfidenceStatus(savedColorMapping)
        };

        return includeCheckboxState ? { ...row, isChecked: false } : row;
    });
// #endregion

// #region SCOPED STYLES
const StyledPOSColorsTablePopup = styled.div`
    & {
        width: 620px;
        text-align: start;
    }

    .popup-shell {
        background: var(--backgrounds-and-lines-n-400, #1e2631);
        border-radius: 8px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        align-items: center;
    }

    .popup-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        align-self: stretch;
        width: 100%;
        padding-top: 9px;
        padding-bottom: 21px;
        border-bottom: 1px solid var(--backgrounds-and-lines-n-100, #3c485a);
        margin-bottom: 8px;
    }

    .header-title-container,
    .header-actions,
    .header-label {
        display: flex;
        align-items: center;
    }

    .header-title-container {
        gap: 8px;
    }

    .header-actions {
        gap: 8px;
    }

    .header-icon {
        width: 16px;
        height: 16px;
        color: var(--text-and-icons-green-500, #00bb00);
    }

    .header-title {
        color: var(--text-and-icons-green-500, #00bb00);
        font-size: 14px;
        font-weight: 400;
        text-transform: uppercase;
        position: relative;
        top: 1px;
    }

    .popup-content {
        display: flex;
        flex-direction: column;
        gap: 14px;
        align-self: stretch;
        width: 100%;
    }

    .bulk-action-section {
        display: flex;
        flex-direction: column;
        gap: 10px;
        align-self: stretch;
    }

    .bulk-action-row {
        display: grid;
        grid-template-columns: 1fr 200px 29px;
        gap: 10px;
        align-items: center;
        padding: 0 8px;
    }

    .bulk-label {
        color: var(--text-and-icons-gray-300, #4f5b6d);
        text-align: right;
        font-size: 14px;
        font-weight: 400;
    }

    .bulk-add-btn {
        background: var(--buttons-all-platforms-gray-500, #10141b);
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 29px;
        height: 27px;
        flex-shrink: 0;
    }

    .bulk-add-btn.enabled:hover {
        box-shadow: 0px 0px 0px 1px #ff8c00;
    }

    .plus {
        width: 16px;
        height: 16px;
    }

    .save-warning {
        color: var(--text-and-icons-orange-500, #ff9900);
        font-size: 12px;
        font-weight: 400;
        text-align: center;
        align-self: stretch;
    }

    .colors-grid-row {
        display: grid;
        grid-template-columns: 36px minmax(100px, 1fr) 200px 120px;
        gap: 10px;
        align-items: center;
        align-self: stretch;
        width: 100%;
        box-sizing: border-box;
        padding: 8px;
    }

    .checkbox-cell {
        display: flex;
        align-items: center;
        justify-content: flex-start;
    }

    .header-label {
        gap: 4px;
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 14px;
        font-weight: 400;
    }

    .divider,
    .divider-2 {
        margin-top: -1px;
        border-style: solid;
        border-color: var(--backgrounds-and-lines-n-100, #3c485a);
        border-width: 1px 0 0 0;
        width: 100%;
        height: 0px;
    }

    .divider-2 {
        border-color: #262e3e;
    }

    .table-scroll-area {
        display: flex;
        align-self: stretch;
        overflow-y: auto;
        max-height: 520px;
    }

    .table-body {
        display: flex;
        flex-direction: column;
        gap: 8px;
        flex: 1;
    }

    .table-item {
        border-radius: 4px;
        display: flex;
        align-items: center;
        //min-height: 35px;
    }

    .table-item.even {
        background: var(--backgrounds-and-lines-n-300, #1a222d);
    }

    .item-name {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: left;
        font-size: 14px;
        font-weight: 400;
    }

    .empty-colors-state {
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: flex-start;
        align-self: stretch;
        padding: 4px 0px 8px 0px;
    }

    .empty-colors-title {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--text-and-icons-orange-500, #ff9900);
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
    }

    .empty-colors-icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
    }

    .empty-colors-description {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        font-size: 14px;
        font-weight: 400;
        line-height: 18px;
    }

    /* Keep the natural height, with room to scroll on short viewports. */
    & {
        display: flex;
        flex-direction: column;
        min-height: 0;
        max-height: calc(100vh - 32px);
        max-height: calc(100dvh - 32px);
    }

    .popup-shell {
        min-height: 0;
        overflow-y: auto;
    }

    .popup-shell > * {
        flex-shrink: 0;
    }

    .table-scroll-area {
        flex-shrink: 1;
        /* Retain a readable scroll target if controls exceed the viewport. */
        min-height: 44px;
    }

    .table-body {
        align-self: flex-start;
    }

    .table-body > * {
        flex-shrink: 0;
    }
`;
// #endregion

export default function POSColorsTablePopup({
    show,
    contextKey = "",
    isContextCurrent,
    onClose,
    colorMappings = EMPTY_ARRAY,
    colorMappingsNeedConfirmation = false,
    setColorMappings,
    setSaveButtonEnabled,
    posExportRequestData
}) {
    // #region STATES
    const [draftColorMappings, setDraftColorMappings] = useState([]);
    const [bulkText, setBulkText] = useState("");
    const [bulkChangeBtnEnabled, setBulkChangeBtnEnabled] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const hasDraftEditsRef = useRef(false);
    const pendingSaveRef = useRef(null);
    const activeSaveStateRef = useRef(null);
    // #endregion

    // #region COMPUTED PROPS
    const {
        labelBrandColors: sourceLabelBrandColors,
        colorsFromPOS = EMPTY_ARRAY
    } = posExportRequestData || {};
    const labelBrandColors = Array.isArray(sourceLabelBrandColors) ? sourceLabelBrandColors : EMPTY_ARRAY;
    const showNoDataMessage = !labelBrandColors?.length;
    const catalogueAvailable = isPOSColorCatalogueAvailable(posExportRequestData);

    const posColorOptions = useMemo(() => {
        return getPOSColorNames(colorsFromPOS).map(name => ({
            id: name,
            name,
            value: name
        }));
    }, [colorsFromPOS]);

    const sourceColorRows = useMemo(() => {
        return buildColorRows({
            labelBrandColors,
            colorMappings,
            colorsFromPOS,
            includeCheckboxState: true
        });
    }, [colorMappings, labelBrandColors, colorsFromPOS]);

    const persistedColorRows = useMemo(() => {
        return buildColorRows({
            labelBrandColors,
            colorMappings,
            colorsFromPOS,
            includeCheckboxState: false
        });
    }, [colorMappings, labelBrandColors, colorsFromPOS]);

    const hasChecked = draftColorMappings.some(color => color.isChecked);
    const allChecked = draftColorMappings.length > 0 && draftColorMappings.every(color => color.isChecked);
    const mappingsChanged =
        draftColorMappings.length !== persistedColorRows.length ||
        draftColorMappings.some((color, idx) => {
            const savedColor = persistedColorRows[idx];

            return color.labelBrandName !== savedColor?.labelBrandName || color.exportName !== savedColor?.exportName;
        });
    const allColorsMapped = catalogueAvailable && areColorsMapped(labelBrandColors, draftColorMappings, colorsFromPOS);
    const needsReview = color => catalogueAvailable && !!color.exportName && !posColorOptions.some(option => option.name === color.exportName);
    const hasStaleColors = draftColorMappings.some(needsReview);
    const colorStatusMessage = hasStaleColors
        ? "Some saved POS colours are no longer available. Review their mappings before saving."
        : getColorMappingStatus(posExportRequestData, draftColorMappings);
    const isSaveEnabled =
        !showNoDataMessage && !hasChecked && allColorsMapped && (mappingsChanged || colorMappingsNeedConfirmation);
    const showSaveWarning = !showNoDataMessage && (hasChecked || !allColorsMapped);
    const isBulkChangeBtnEnabled = bulkChangeBtnEnabled && hasChecked;
    activeSaveStateRef.current = { show, contextKey, isSaveEnabled, isContextCurrent, labelBrandColors, colorsFromPOS };
    // #endregion

    // #region METHODS
    const cancelPendingSave = () => {
        if (pendingSaveRef.current) clearTimeout(pendingSaveRef.current.id);
        pendingSaveRef.current = null;
    };

    const handleCloseClicked = () => {
        cancelPendingSave();
        setIsSaving(false);
        hasDraftEditsRef.current = false;
        setDraftColorMappings([]);
        setBulkText("");
        setBulkChangeBtnEnabled(false);
        onClose?.();
    };

    const updateDraftColorMappings = updater => {
        cancelPendingSave();
        setIsSaving(false);
        hasDraftEditsRef.current = true;
        setDraftColorMappings(updater);
    };

    const handleBulkAdd = () => {
        if (!isBulkChangeBtnEnabled) return;

        updateDraftColorMappings(prev =>
            prev.map(color => {
                if (!color.isChecked) return color;
                return {
                    ...color,
                    exportName: bulkText,
                    confidence: undefined,
                    confidenceStatus: "manual",
                    isChecked: false
                };
            })
        );
        setBulkText("");
        setBulkChangeBtnEnabled(false);
    };

    const onSaveClicked = () => {
        if (!isSaveEnabled || isSaving || pendingSaveRef.current) return;

        setIsSaving(true);
        const pending = { contextKey };
        pendingSaveRef.current = pending;
        pending.id = setTimeout(() => {
            const active = activeSaveStateRef.current;
            if (pendingSaveRef.current !== pending) return;
            cancelPendingSave();
            if (!active.show || active.contextKey !== pending.contextKey || !active.isSaveEnabled ||
                !areColorsMapped(active.labelBrandColors, draftColorMappings, active.colorsFromPOS) ||
                (active.isContextCurrent && !active.isContextCurrent(pending.contextKey))) {
                setIsSaving(false);
                return;
            }
            setColorMappings?.(
                draftColorMappings.map(color => ({
                    labelBrandName: color.labelBrandName,
                    exportName: color.exportName
                }))
            );
            setSaveButtonEnabled?.(true);
            setIsSaving(false);
            handleCloseClicked();
        }, 500);
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        cancelPendingSave();
        setIsSaving(false);
        return cancelPendingSave;
    }, [show, contextKey]);

    useEffect(() => {
        if (!show) { hasDraftEditsRef.current = false; return; }
        if (hasDraftEditsRef.current) return;

        setDraftColorMappings(sourceColorRows);
        setBulkText("");
        setBulkChangeBtnEnabled(false);
    }, [show, sourceColorRows]);
    // #endregion

    // #region RENDER
    return (
        <FullScreenOverlay show={show}>
            <StyledPOSColorsTablePopup className="colors-table-popup">
                <div className="popup-shell">
                    <div className="popup-header">
                        <div className="header-title-container">
                            <Palette className="header-icon" />
                            <div className="header-title">CUSTOMIZE COLORS</div>
                        </div>
                        <div className="header-actions">
                            <BtnLargeSolid
                                title="CLOSE"
                                onClick={handleCloseClicked}
                                normalColor="var(--Buttons-All-Platforms-Gray-500)"
                                hoverColor="var(--Buttons-All-Platforms-Gray-500)"
                                hoverBorderColor="var(--text-icons-orange-500)"
                                textStyle={{
                                    fontSize: "12px",
                                    textTransform: "uppercase"
                                }}
                                style={{
                                    width: "67px",
                                    height: "30px",
                                    padding: "0 10px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            />
                            <BtnLargeSolid
                                title={isSaving ? <SimpleLoadingSpinner /> : "SAVE"}
                                onClick={onSaveClicked}
                                disabled={!isSaveEnabled || isSaving}
                                normalColor="var(--buttons-all-platforms-orange-500, #FF9800)"
                                hoverColor="var(--buttons-all-platforms-orange-400, #FF9800)"
                                disabledColor="var(--buttons-all-platforms-gray-500, #10141b)"
                                textStyle={{
                                    fontSize: "12px",
                                    textTransform: "uppercase",
                                    color:
                                        !isSaveEnabled || isSaving
                                            ? "var(--text-and-icons-gray-300, #4f5b6d)"
                                            : "#ffffff"
                                }}
                                style={{
                                    width: "67px",
                                    height: "30px",
                                    padding: "0 10px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                            />
                        </div>
                    </div>

                    {colorStatusMessage && !showNoDataMessage && (
                        <div className="save-warning" role="status">{colorStatusMessage}</div>
                    )}
                    {showNoDataMessage && (
                        <div className="empty-colors-state" role="status">
                            <div className="empty-colors-title">
                                <AlertCircle className="empty-colors-icon" />
                                <span>NO DATA YET</span>
                            </div>
                            <div className="empty-colors-description">
                                No brand colour data is available yet. Once colours are available, you can link them to your POS colours.
                            </div>
                        </div>
                    )}

                    {!showNoDataMessage && (
                        <>
                            <div className="popup-content">
                                <div className="bulk-action-section">
                                    <div className="bulk-action-row">
                                        <div
                                            className="bulk-label"
                                            style={{
                                                color: hasChecked
                                                    ? "var(--text-and-icons-gray-100, #d1d1d1)"
                                                    : "var(--text-and-icons-gray-300, #4f5b6d)"
                                            }}>
                                            Map selected Brand colors to POS color:
                                        </div>
                                        <FormField
                                            type="dropdown"
                                            dropdownWithinViewport={true}
                                            varName="color-bulk-change"
                                            styling={{
                                                formFieldWidth: "100%",
                                                formFieldHeight: "27px",
                                                marginBottom: "0px",
                                                dropdownOverlayTop: "35px"
                                            }}
                                            placeholder="Select yours"
                                            isDropdownMultiselect={false}
                                            isDropdownSearchable={true}
                                            dropdownData={posColorOptions}
                                            value={hasChecked ? bulkText : ""}
                                            onDropdownChange={item => {
                                                setBulkText(item.name);
                                                setBulkChangeBtnEnabled(true);
                                            }}
                                            requiredType={hasChecked ? "simple" : "disabledSimple"}
                                        />
                                        <div
                                            className={`bulk-add-btn ${isBulkChangeBtnEnabled ? "enabled cursor-pointer" : "disabled hover-not-allowed"}`}
                                            onClick={isBulkChangeBtnEnabled ? handleBulkAdd : null}>
                                            <Plus
                                                className="plus"
                                                color={
                                                    isBulkChangeBtnEnabled
                                                        ? "#FFFFFF"
                                                        : "var(--text-and-icons-gray-300, #7C868B)"
                                                }
                                            />
                                        </div>
                                    </div>

                                    {showSaveWarning && (
                                        <div className="save-warning">
                                            SAVE is only enabled when all brand's colors are linked to a POS color!
                                        </div>
                                    )}
                                </div>

                                <div className="colors-grid-row">
                                    <div className="checkbox-cell">
                                        <CheckboxVector
                                            checkboxColor="#00F260"
                                            onCheckboxClick={() => {
                                                const newValue = !allChecked;
                                                updateDraftColorMappings(prev =>
                                                    prev.map(color => ({ ...color, isChecked: newValue }))
                                                );
                                            }}
                                            isChecked={allChecked}
                                        />
                                    </div>
                                    <div className="header-label">
                                        Brand's Color
                                        <TooltipWithInfoCircle
                                            iconSize={"14px"}
                                            enabled={true}
                                            header="Brand's Color"
                                            title={
                                                <>
                                                    These are the color names provided by the Label Brand in the product
                                                    master.
                                                    <br />
                                                    Each brand color must be linked to a POS color before the color
                                                    rules can be saved.
                                                    <br />
                                                    <br />
                                                    This does not change the original color name in BOtag. It only
                                                    defines how products with this brand color should be exported to
                                                    Front Systems.
                                                </>
                                            }
                                            width="350px"
                                        />
                                    </div>
                                    <div className="header-label">
                                        Color to POS
                                        <TooltipWithInfoCircle
                                            iconSize={"14px"}
                                            enabled={true}
                                            header="Color to POS"
                                            title={
                                                <>
                                                    Select which Front Systems color this brand color should be exported
                                                    as.
                                                    <br />
                                                    The dropdown shows the colors available in your POS color list.
                                                    <br />
                                                    <br />
                                                    Each brand color must be linked to a POS color before the color
                                                    rules can be saved.
                                                    <br />
                                                    <br />
                                                    This does not change the original brand color in BOtag. It only
                                                    controls the color value sent to Front Systems.
                                                </>
                                            }
                                            width="350px"
                                        />
                                    </div>
                                    <div className="header-label">
                                        Confidence
                                        <TooltipWithInfoCircle
                                            iconSize={"14px"}
                                            enabled={true}
                                            header="Confidence"
                                            title={
                                                <>
                                                    AI confidence is shown for suggested color matches generated by
                                                    BOtag.
                                                    <br />
                                                    Manually edited suggestions show "--" until they are saved.
                                                    <br />
                                                    Saved mappings show "Saved" because they are confirmed settings, not
                                                    AI suggestions.
                                                </>
                                            }
                                            width="350px"
                                        />
                                    </div>
                                </div>
                                <div className="divider-2"></div>
                            </div>

                            <div className="table-scroll-area">
                                <div className="table-body">
                                    {draftColorMappings.map((color, idx) => {
                                        const confidenceDisplay = getConfidenceDisplay(needsReview(color)
                                            ? { ...color, confidenceStatus: "review" } : color);

                                        return (
                                            <div
                                                className={`table-item ${idx % 2 === 0 ? "even" : ""}`}
                                                key={`${color.labelBrandName}-${idx}`}>
                                                <div className="colors-grid-row">
                                                    <div className="checkbox-cell">
                                                        <CheckboxVector
                                                            checkboxColor="#00F260"
                                                            onCheckboxClick={() => {
                                                                updateDraftColorMappings(prev =>
                                                                    prev.map((item, i) =>
                                                                        i === idx
                                                                            ? { ...item, isChecked: !item.isChecked }
                                                                            : item
                                                                    )
                                                                );
                                                            }}
                                                            isChecked={color.isChecked || false}
                                                        />
                                                    </div>
                                                    <div className="item-name">{color.labelBrandName}</div>
                                                    <FormField
                                                        type="dropdown"
                                                        dropdownWithinViewport={true}
                                                        varName={`color-dropdown-${idx}`}
                                                        styling={{
                                                            formFieldWidth: "100%",
                                                            formFieldHeight: "27px",
                                                            marginBottom: "0px",
                                                            dropdownOverlayTop: "35px"
                                                        }}
                                                        placeholder="Select yours"
                                                        isDropdownMultiselect={false}
                                                        isDropdownSearchable={true}
                                                        dropdownData={posColorOptions}
                                                        value={color.exportName}
                                                        onDropdownChange={item => {
                                                            updateDraftColorMappings(prev =>
                                                                prev.map((colorItem, i) =>
                                                                    i === idx
                                                                        ? {
                                                                              ...colorItem,
                                                                              exportName: item.name,
                                                                              confidence: undefined,
                                                                              confidenceStatus: "manual"
                                                                          }
                                                                        : colorItem
                                                                )
                                                            );
                                                        }}
                                                        requiredType="none"
                                                        useDropdownPortal={true}
                                                    />
                                                    <FormField
                                                        type="text"
                                                        varName={`color-confidence-${idx}`}
                                                        styling={{
                                                            formFieldWidth: "100%",
                                                            formFieldHeight: "27px",
                                                            marginBottom: "0px",
                                                            textColorOverride: confidenceDisplay.color
                                                        }}
                                                        value={confidenceDisplay.value}
                                                        requiredType="disabledSimple"
                                                        disableTooltip={true}
                                                        disableAssistiveText={true}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </StyledPOSColorsTablePopup>
        </FullScreenOverlay>
    );
    // #endregion
}
